import React from 'react';

export default function Features() {
  return (
    <section className="features" id="features">
      <div className="container">
        <div className="section-label">How it works</div>
        <h2 className="section-title">From bill rate to full cost breakdown<br/>in seconds</h2>
        <p className="section-sub">
          Stop estimating. Quoteology calculates every statutory cost — employer taxes, social insurance,
          pension, housing funds — using real progressive tax brackets for each country, not rough percentages.
        </p>
        <div className="feature-blocks">
          <FeatureBlock
            title="Actual Tax engine"
            body={[
              "Most platforms use a flat rate estimate. Quoteology implements the real tax code — progressive income tax slabs, employment income deductions, social insurance thresholds — for every country it supports. Japan's trinnskatt. Norway's minstefradrag. Australia's ATO brackets. The Netherlands' 30% ruling.",
              "Your quotes are accurate. Your margins are protected."
            ]}
            visual={<TaxVisual/>}
          />
          <FeatureBlock
            flip
            title="Multi-country comparison"
            body={[
              "Compare the true employer cost of the same contractor placed in different countries, side by side. Instantly see where the margin is strongest. Export the full comparison as a branded PDF or Excel deck to share with clients.",
              "Win the mandate by showing you've done the maths they haven't."
            ]}
            visual={<CompareVisual/>}
          />
          <FeatureBlock
            title="Admin-configurable rates"
            body={[
              "Tax rates change every year. Quoteology's admin panel lets you update every statutory rate — social insurance percentages, payroll tax thresholds, housing fund city tables — without touching code. Your platform stays current without waiting for a developer.",
              "Rates are yours to own. Calculations are always accurate."
            ]}
            visual={<AdminVisual/>}
          />
          <FeatureBlock
            flip
            title="Branded PDF & Excel exports"
            body={[
              "Every calculation exports as a polished, client-ready PDF or Excel report. Single-country detailed breakdowns. Multi-country comparison grids. Formatted consistently so every quote you send looks the same — professional and trustworthy."
            ]}
            visual={<ExportVisual/>}
          />
        </div>
      </div>
    </section>
  );
}

function FeatureBlock({ title, body, visual, flip }) {
  return (
    <div className={`feature-block${flip ? ' feature-block--flip' : ''}`}>
      <div className="feature-block-text">
        <h3>{title}</h3>
        {body.map((p, i) => <p key={i}>{p}</p>)}
      </div>
      <div className="feature-block-visual">{visual}</div>
    </div>
  );
}

function TaxVisual() {
  return (
    <div className="tax-visual">
      {[
        ['Employment Income Deduction', '- ¥1,320,000', 'tx-muted'],
        ['Basic Deduction',             '- ¥480,000',   'tx-muted'],
        ['SS Deduction',               '- ¥524,100',   'tx-muted'],
      ].map(([l, v, cls]) => (
        <div className="tax-row" key={l}>
          <span className="tax-label">{l}</span>
          <span className={`tax-val ${cls}`}>{v}</span>
        </div>
      ))}
      <div className="tax-divider"/>
      <div className="tax-row">
        <span className="tax-label">Taxable Income</span>
        <span className="tax-val tx-blue">¥3,499,900</span>
      </div>
      {[
        ['Income Tax',          '¥291,200'],
        ['Resident Tax (10%)', '¥349,990'],
      ].map(([l, v]) => (
        <div className="tax-row" key={l}>
          <span className="tax-label">{l}</span>
          <span className="tax-val">{v}</span>
        </div>
      ))}
      <div className="tax-badge">Actual Tax · Japan</div>
    </div>
  );
}

function CompareVisual() {
  const rows = [
    { flag:'🇸🇦', name:'Saudi Arabia', cost:'SAR 182,400', chip:'chip-green',  label:'Lowest'  },
    { flag:'🇳🇱', name:'Netherlands',  cost:'€ 94,320',   chip:'chip-yellow', label:'Mid'     },
    { flag:'🇯🇵', name:'Japan',        cost:'¥ 7,809,312',chip:'chip-orange', label:'Higher'  },
    { flag:'🇳🇴', name:'Norway',       cost:'NOK 628,400',chip:'chip-red',    label:'Highest' },
  ];
  return (
    <div className="compare-visual">
      <div className="compare-header"><span>Country</span><span>Total Cost</span><span>Tax Rate</span></div>
      {rows.map((r, i) => (
        <div className={`compare-row${i === 0 ? ' best' : ''}`} key={r.name}>
          <span>{r.flag} {r.name}</span>
          <span>{r.cost}</span>
          <span className={`chip ${r.chip}`}>{r.label}</span>
        </div>
      ))}
    </div>
  );
}

function AdminVisual() {
  const rows = [
    { key:'jp_social_health_pct',  val:'9.98%',  active:false },
    { key:'jp_pension_pct',        val:'18.30%', active:false },
    { key:'no_trinnskatt_band_1',  val:'1.70%',  active:false },
    { key:'no_minstefradrag_pct',  val:'45.00%', active:true  },
    { key:'us_futa_rate',          val:'0.60%',  active:false },
  ];
  return (
    <div className="admin-visual">
      {rows.map(r => (
        <div className={`admin-row${r.active ? ' admin-row--active' : ''}`} key={r.key}>
          <span className="admin-key">{r.key}</span>
          <span className="admin-val">{r.val}</span>
          <span className={`admin-edit${r.active ? ' editing' : ''}`}>{r.active ? 'Saving…' : 'Edit'}</span>
        </div>
      ))}
    </div>
  );
}

function ExportVisual() {
  return (
    <div className="export-visual">
      <div className="export-doc">
        <div className="export-doc-header">
          <div className="export-doc-logo">Q</div>
          <div>
            <div className="export-doc-title">Contractor Cost Report</div>
            <div className="export-doc-sub">Japan — Tokyo · Actual Tax</div>
          </div>
        </div>
        <div className="export-doc-bar"/>
        <div className="export-doc-rows">
          {[
            ['Gross annual',  '¥5,824,000'],
            ['Employer SS',   '¥1,063,712'],
            ['Income tax',    '¥291,200'],
          ].map(([l, v]) => (
            <div className="export-doc-row" key={l}><span>{l}</span><span>{v}</span></div>
          ))}
          <div className="export-doc-row export-doc-row--total">
            <span>Total cost</span><span>¥7,809,312</span>
          </div>
        </div>
      </div>
      <div className="export-chips">
        <span className="export-chip pdf">PDF</span>
        <span className="export-chip excel">Excel</span>
      </div>
    </div>
  );
}